import {
  toVerticalProgressBarBackgroundColor,
  VerticalProgressBar,
  VerticalProgressBarBackgroundColors,
} from "@playlive/overlay-core/progress-bars/vertical";
import { deepSignal } from "deepsignal";
import Picker from "@/components/input/picker.input.jsx";
import YesNoButtonInput from "@/components/input/yes-no-button.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import DemoAnimationsDropdown from "@/components/overlays/shared/demo-animations-dropdown.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import HorizontalCustomizeWrapper from "@/components/overlays/wrapper/horizonal-customize-wrapper.jsx";
import ScaledOverlayWrapper from "@/components/overlays/wrapper/scaled-overlay-wrapper.jsx";
import ColorPicker from "@/components/pickers/color-picker/color.picker.jsx";
import ColorPickerLabel from "@/components/pickers/color-picker/color-picker-label.jsx";
import CharitySection from "@/components/shared/charity-section.jsx";
import useAdjustableAmounts from "@/hooks/useAdjustableAmounts.js";
import useCommon from "@/hooks/useCommon.js";
import usePreviewAnimations from "@/hooks/usePreviewAnimations.js";
import { charityIsTiltify, isYes } from "@/util/common.js";
import { CHARITY } from "@/util/const.js";

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  bgColor: "default-red",
  verticalType: "rounded",
  showOriginalGoal: "yes",
  ccamMode: "no",
});

const useBetaProgressBars = isYes(import.meta.env.VITE_BETA_PROGRESS_BARS || "no");
const urlBase = useBetaProgressBars ? "progress-bars/vertical" : "vertical";

export default function VerticalProgressBarPage() {
  const { overlayInfo, overlayURL, setConfigState } = useCommon({
    config,
    urlBase,
  });

  const { currentAmount, goalAmount, setCurrentAmount, setGoalAmount } = useAdjustableAmounts();

  const themes = [
    { name: "3D", value: "3d" },
    { name: "Slanted", value: "slanted" },
    { name: "Rounded", value: "rounded" },
  ];

  const { isPreviewingAnimation, startAnimation } = usePreviewAnimations({
    setCurrentAmount,
    setGoalAmount,
  });

  function filterColors(color) {
    return VerticalProgressBarBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay relative w-full">
          <ScaledOverlayWrapper
            defaultScale={"scale-65"}
            allowScaling={false}
            innerClassName={"h-[550px]"}
            addPadding={config.verticalType !== "3d"}
            addPosition={config.verticalType === "3d" ? "absolute bottom-0" : ""}
          >
            <div
              className={` ${config.verticalType === "3d" && "absolute bottom-0 h-full"} max-w-full w-full`}
            >
              <VerticalProgressBar
                goalAmount={goalAmount}
                currentAmount={currentAmount}
                showOriginalGoal={isYes(config.showOriginalGoal)}
                ccamMode={isYes(config.ccamMode)}
                backgroundColor={
                  isYes(config.ccamMode)
                    ? toVerticalProgressBarBackgroundColor("light-yellow")
                    : toVerticalProgressBarBackgroundColor(config.bgColor)
                }
                type={config.verticalType}
                className={`${config.verticalType === "3d" && "absolute bottom-[40px] left-0 right-0"}`}
                originalGoalAmount={1000}
              />
            </div>
          </ScaledOverlayWrapper>
          <DemoAnimationsDropdown
            onChosen={startAnimation}
            milestone={false}
            disabled={isPreviewingAnimation}
          />
        </div>

        <HorizontalCustomizeWrapper className="flex flex-col mb-0!">
          <CharitySection onChange={setConfigState} charityType={config.charityType} />

          <CustomizeSection className="flex-grow" title="Appearance">
            <ColorPickerLabel
              color={config.bgColor}
              labelText="Progress bar"
              className={
                isYes(config.ccamMode)
                  ? "opacity-50 transition-opacity"
                  : "opacity-100 transition-opacity"
              }
            />
            <ColorPicker
              disabled={isYes(config.ccamMode)}
              currentColor={config.bgColor}
              fieldName="bgColor"
              filterFunction={filterColors}
              onChange={setConfigState}
            />

            <Picker
              onChange={setConfigState}
              currentValue={config.verticalType}
              items={themes}
              fieldName="verticalType"
              labelText="Theme"
            />

            {/* <YesNoButtonInput
              className="flex-1"
              onChange={setConfigState}
              mode="checkbox"
              currentValue={config.ccamMode}
              name="ccamMode"
              label="CCAM Mode"
              labelNoClass="text-black"
              buttonNoClass="bg-warning opacity-60 shadow-none"
              buttonYesClass="bg-warning opacity-100 shadow-none"
              labelYesClass="text-black"
            /> */}

            <div className="my-2">
              <div className="flex flex-col md:flex-row mt-2">
                <YesNoButtonInput
                  className="md:mr-4 mb-2 md:mb-0 flex-1"
                  onChange={setConfigState}
                  mode="checkbox"
                  currentValue={config.showOriginalGoal}
                  name="showOriginalGoal"
                  label="Show Next Goal Progress"
                  labelTextClassName="text-white"
                  hidden={!charityIsTiltify(config.charityType)}
                />
              </div>

              <p className="pl-1 pt-1">
                When enabled, the progress bar will celebrate reaching your original goal and
                automatically extend to show progress toward your new goal if it's updated.
              </p>
            </div>
          </CustomizeSection>
        </HorizontalCustomizeWrapper>
      </div>

      <LinkSetup
        allowTeamCampaignSelection={true}
        constrainWidth={false}
        charityType={config.charityType}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
      />
    </div>
  );
}
